/**
 * Educational content for Favicon Generator page
 */

export const FaviconEducational = () => {
  return (
    <div className="space-y-8 mb-8">
      {/* Introduction Section */}
      <section className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold text-white mb-4">
          Generate Complete Favicon Packages for All Devices & Browsers
        </h2>
        <p className="text-slate-300 mb-4">
          Create a comprehensive favicon package with all the sizes and formats needed for modern websites and 
          applications. Our favicon generator automatically converts your image into multiple resolutions, generates 
          ICO files, creates Apple Touch icons, Android Chrome icons, and produces ready-to-use manifest files—all 
          from a single upload.
        </p>
        <p className="text-slate-300">
          Favicons are small icons that represent your website in browser tabs, bookmarks, search results, and home 
          screens. Having properly sized favicons for every platform ensures your brand appears professional and 
          recognizable across all devices, from desktop browsers to mobile home screens.
        </p>
      </section>

      {/* Why Favicons Matter Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Why Favicons Are Essential for Your Website
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">🎯 Brand Recognition</h3>
            <p className="text-slate-300 text-sm">
              Favicons make your site instantly recognizable in browser tabs and bookmarks. Users can quickly identify 
              your site among dozens of open tabs, improving navigation and reinforcing brand identity with every visit.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">💼 Professional Appearance</h3>
            <p className="text-slate-300 text-sm">
              Sites without favicons appear unfinished or amateur. A proper favicon package demonstrates attention to 
              detail and professionalism, building trust with visitors before they even interact with your content.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">📱 Mobile Experience</h3>
            <p className="text-slate-300 text-sm">
              Apple Touch icons and Android Chrome icons appear when users add your site to their home screen, making 
              it feel like a native app. High-quality icons encourage users to create shortcuts for quick access.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">🔍 SEO Benefits</h3>
            <p className="text-slate-300 text-sm">
              Favicons appear in search results on mobile devices, making your listing more noticeable and clickable. 
              They also help with brand recall when users see your link in search results or when shared on social media.
            </p>
          </div>
        </div>
      </section>

      {/* Sizes Explained Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Understanding Favicon Sizes & Their Uses
        </h2>
        <p className="text-slate-300 mb-4">
          Different devices and browsers require different favicon sizes. Our generator creates all standard sizes 
          automatically. Here's what each size is used for:
        </p>
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">16×16 & 32×32 pixels</h3>
              <span className="text-xs bg-indigo-600 px-2 py-1 rounded">Browser Tabs</span>
            </div>
            <p className="text-slate-300 text-sm">
              The classic favicon sizes displayed in browser tabs, address bars, and bookmark lists. 16×16 is used for 
              standard displays, while 32×32 provides better clarity on high-DPI screens like Retina displays.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">48×48 pixels</h3>
              <span className="text-xs bg-indigo-600 px-2 py-1 rounded">Windows Sites</span>
            </div>
            <p className="text-slate-300 text-sm">
              Used by Windows when users pin your site to their taskbar or start menu. This size ensures your icon 
              looks clear when used as a desktop shortcut in Windows 10 and Windows 11.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">64×64, 128×128 pixels</h3>
              <span className="text-xs bg-indigo-600 px-2 py-1 rounded">Desktop Apps</span>
            </div>
            <p className="text-slate-300 text-sm">
              Medium sizes used in various desktop contexts—browser new tab pages, bookmark managers, and some browser 
              extensions. These sizes provide flexibility across different browser interfaces.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">180×180 pixels (Apple Touch Icon)</h3>
              <span className="text-xs bg-indigo-600 px-2 py-1 rounded">iOS/Safari</span>
            </div>
            <p className="text-slate-300 text-sm">
              Required for iOS devices when users add your site to their home screen. Safari automatically rounds the 
              corners, so design your icon with this in mind. This size also appears in Safari's favorites.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">192×192, 512×512 pixels</h3>
              <span className="text-xs bg-indigo-600 px-2 py-1 rounded">Android/PWA</span>
            </div>
            <p className="text-slate-300 text-sm">
              Android Chrome icons for Progressive Web Apps (PWAs) and home screen shortcuts. 192×192 is the minimum, 
              512×512 is recommended for better quality on high-resolution Android devices and splash screens.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">favicon.ico (multi-size)</h3>
              <span className="text-xs bg-indigo-600 px-2 py-1 rounded">Legacy Support</span>
            </div>
            <p className="text-slate-300 text-sm">
              ICO format containing multiple sizes (16, 32, 48) in one file. Required for Internet Explorer and older 
              browsers. Modern browsers fall back to ICO if PNG versions aren't found.
            </p>
          </div>
        </div>
      </section>

      {/* Design Tips Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Design Tips for Perfect Favicons
        </h2>
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Keep it simple:</strong> Favicons are displayed at very small sizes. 
                Avoid intricate details that won't be visible. Use bold shapes, simple symbols, or distinctive letters 
                that remain recognizable at 16×16 pixels.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Use high contrast:</strong> Ensure your favicon stands out against both 
                light and dark browser themes. Test on different backgrounds—what works on white may disappear on black.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Consider transparent backgrounds:</strong> PNG favicons with transparent 
                backgrounds adapt to any browser theme. However, ensure your icon has enough contrast to remain visible.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Start with at least 512×512:</strong> Upload a high-resolution source 
                image (minimum 512×512, preferably 1024×1024) to ensure crisp results when downscaled to smaller sizes.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Use your brand colors:</strong> Favicons reinforce brand identity. Use 
                your primary brand color or logo to maintain consistency across all touchpoints.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Test at small sizes:</strong> After generation, view your favicon at 
                actual size (16×16) to ensure it's legible. What looks perfect at 512×512 may be muddy when tiny.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Account for rounded corners:</strong> iOS Safari automatically rounds 
                Apple Touch Icons. Keep important elements away from corners, with at least 10% padding around the edges.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Implementation Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          How to Implement Your Favicon Package
        </h2>
        <p className="text-slate-300 mb-4">
          After generating your favicon package, follow these steps to add it to your website:
        </p>
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">1. Upload Files to Your Server</h3>
            <p className="text-slate-300 text-sm mb-2">
              Place all generated files in your website's root directory or in a dedicated /icons folder. The standard 
              location is your root directory for maximum compatibility.
            </p>
            <code className="block bg-slate-900 p-2 rounded text-xs text-slate-300 overflow-x-auto">
              /favicon.ico<br/>
              /favicon-16x16.png<br/>
              /favicon-32x32.png<br/>
              /apple-touch-icon.png<br/>
              /android-chrome-192x192.png<br/>
              /android-chrome-512x512.png
            </code>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">2. Add HTML Link Tags</h3>
            <p className="text-slate-300 text-sm mb-2">
              Insert these link tags in your HTML &lt;head&gt; section. They tell browsers which files to use for 
              different contexts and devices.
            </p>
            <code className="block bg-slate-900 p-2 rounded text-xs text-slate-300 overflow-x-auto">
              {`<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">
<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">
<link rel="manifest" href="/site.webmanifest">`}
            </code>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">3. Configure Manifest Files</h3>
            <p className="text-slate-300 text-sm">
              The site.webmanifest and browserconfig.xml files are included in your package. These configure how your 
              icon appears on Android and Windows devices. Ensure they reference the correct icon paths.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">4. Test Across Devices</h3>
            <p className="text-slate-300 text-sm">
              Verify your favicons appear correctly in Chrome, Firefox, Safari, and Edge. Test on both desktop and 
              mobile, and try adding your site to a mobile home screen to check the icon quality.
            </p>
          </div>
        </div>
      </section>

      {/* Common Issues Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Troubleshooting Common Favicon Issues
        </h2>
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Favicon Doesn't Update</h3>
            <p className="text-slate-300 text-sm">
              Browsers aggressively cache favicons. Clear your browser cache, use hard refresh (Ctrl+Shift+R or Cmd+Shift+R), 
              or test in incognito mode. Sometimes it takes up to 24 hours for changes to propagate fully.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Wrong Icon Appearing</h3>
            <p className="text-slate-300 text-sm">
              Check that your HTML link tags have correct paths and that files are uploaded to the right location. 
              Verify file permissions allow public access. Use browser DevTools Network tab to check for 404 errors.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Blurry on Retina Displays</h3>
            <p className="text-slate-300 text-sm">
              Ensure you're providing 32×32 PNG favicons for high-DPI displays. If you only have 16×16, browsers will 
              upscale it, causing blur. Always generate all recommended sizes from a high-resolution source.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
